import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionParamOptions } from '@/api/onsiteLiveSubscriptions';
import type { SubscriptionParamKind, SubscriptionParamOption } from '@/types/oscar';
import { btnPrimary, btnSecondary, inputCls } from '../BranchRelationships/styles';
import { formatBranchParam, parseBranchParam, splitIds, type BranchScope } from './schema';

/**
 * The OnBudget subscription parameter pickers (View/OnsiteSupport/Subscription/Select*),
 * as an inline panel under the field. Each writes the value in OnBudget's exact text
 * format, because the SSRS subscription views parse it:
 *   customer  - Param1, one customer code (search)
 *   sites     - Param2, the customer's site codes "A1,B2" (needs Param1)
 *   branch    - Param3, "1200,1201" | "Branches In Area 3,5" | "Branches In Region 1"
 *   salesreps - Param4, sales rep codes "AB,CD"
 * The field itself stays editable: a list the server cannot read yet (503) says so,
 * and the value can be typed.
 */
export type PickerKind = 'customer' | 'sites' | 'branch' | 'salesreps';

export function SubscriptionParamPicker({
  kind, value, customer, onApply, onClose,
}: {
  kind: PickerKind;
  value: string;
  /** Param1 - the sites list is that customer's. */
  customer?: string;
  onApply: (value: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="mt-1 rounded border border-navy/30 bg-[#F4F6F9] p-2" role="group" aria-label={`Pick ${kind}`}>
      {kind === 'customer' && <CustomerList onPick={(code) => { onApply(code); onClose(); }} />}
      {kind === 'sites' && (
        customer?.trim()
          ? <Checklist listKind="sites" customer={customer} initial={splitIds(value)} onApply={(ids) => { onApply(ids.join(',')); onClose(); }} />
          : <p className="text-xs text-darkGrey/80">Enter or pick the customer (parameter 1) first.</p>
      )}
      {kind === 'branch' && <BranchChecklist value={value} onApply={(v) => { onApply(v); onClose(); }} />}
      {kind === 'salesreps' && (
        <Checklist listKind="salesreps" initial={splitIds(value).map((i) => i.toUpperCase())} onApply={(ids) => { onApply(ids.join(',')); onClose(); }} />
      )}
      <div className="mt-2 text-right">
        <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function CustomerList({ onPick }: { onPick: (code: string) => void }) {
  const [search, setSearch] = useState('');
  const term = search.trim();
  const q = useQuery({
    queryKey: ['onsitelive-admin', 'subscription-options', 'customers', term],
    queryFn: ({ signal }) => getSubscriptionParamOptions('customers', { search: term }, signal),
    enabled: term.length >= 2,
    retry: false,
  });
  return (
    <div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Customer code or name"
        aria-label="Search customers"
        className={`${inputCls} w-full`}
      />
      <ListState q={q} empty={term.length < 2 ? 'Type at least 2 characters.' : 'No customer matches.'} />
      <ul className="mt-1 max-h-48 overflow-y-auto text-sm">
        {(q.data ?? []).map((o) => (
          <li key={o.Id}>
            <button type="button" className="w-full px-2 py-1 text-left hover:bg-white" onClick={() => onPick(o.Id)}>
              <span className="font-mono font-semibold text-navy">{o.Id}</span> <span className="text-darkGrey/80">{o.Name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BranchChecklist({ value, onApply }: { value: string; onApply: (value: string) => void }) {
  const parsed = useMemo(() => parseBranchParam(value), [value]);
  const [scope, setScope] = useState<BranchScope>(parsed.scope);
  const listKind: SubscriptionParamKind = scope;
  return (
    <div>
      <label className="flex items-center gap-2 text-xs font-semibold text-darkGrey">
        Branch format
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value as BranchScope)}
          className={`${inputCls} font-normal`}
          aria-label="Branch format"
        >
          <option value="branches">Branches</option>
          <option value="areas">Branches In Area</option>
          <option value="regions">Branches In Region</option>
        </select>
      </label>
      <Checklist
        key={scope}
        listKind={listKind}
        initial={scope === parsed.scope ? parsed.ids : []}
        onApply={(ids) => onApply(formatBranchParam(scope, ids))}
      />
    </div>
  );
}

/** Tick, filter, apply. Grouped by region when the list carries one. */
function Checklist({
  listKind, customer, initial, onApply,
}: {
  listKind: SubscriptionParamKind;
  customer?: string;
  initial: string[];
  onApply: (ids: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>(initial);
  const [filter, setFilter] = useState('');
  const q = useQuery({
    queryKey: ['onsitelive-admin', 'subscription-options', listKind, customer ?? ''],
    queryFn: ({ signal }) => getSubscriptionParamOptions(listKind, { customer }, signal),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const f = filter.trim().toLowerCase();
  const rows = (q.data ?? []).filter((o) => !f || o.Id.toLowerCase().includes(f) || o.Name.toLowerCase().includes(f));
  const groups = [...new Set(rows.map((o) => o.Group ?? ''))];
  // Codes already in the field that the list does not know - kept, and shown, so applying never drops them.
  const unknown = picked.filter((id) => q.data && !q.data.some((o) => o.Id.toUpperCase() === id.toUpperCase()));
  const toggle = (id: string) =>
    setPicked((p) => (p.some((x) => x.toUpperCase() === id.toUpperCase()) ? p.filter((x) => x.toUpperCase() !== id.toUpperCase()) : [...p, id]));
  const isPicked = (o: SubscriptionParamOption) => picked.some((x) => x.toUpperCase() === o.Id.toUpperCase());

  return (
    <div className="mt-1">
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter" aria-label="Filter the list" className={`${inputCls} w-full`} />
      <ListState q={q} empty="Nothing to pick." />
      <div className="mt-1 max-h-56 overflow-y-auto text-sm">
        {groups.map((g) => (
          <div key={g || 'none'}>
            {g && <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-darkGrey/60">{g}</p>}
            {rows.filter((o) => (o.Group ?? '') === g).map((o) => (
              <label key={o.Id} className={`flex items-center gap-1.5 px-1 py-0.5 ${o.Inactive ? 'text-darkGrey/50' : 'text-darkGrey'}`}>
                <input type="checkbox" checked={isPicked(o)} onChange={() => toggle(o.Id)} />
                {o.Name || o.Id}{o.Inactive ? ' (inactive)' : ''}
              </label>
            ))}
          </div>
        ))}
      </div>
      {unknown.length > 0 && (
        <p className="mt-1 text-xs text-darkGrey/70">Also kept (not in the list): {unknown.join(', ')}</p>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button type="button" className={btnPrimary} onClick={() => onApply(picked)} disabled={!q.data}>
          Use {picked.length} selected
        </button>
        <span className="text-xs text-darkGrey/70">{picked.length === 0 ? 'Nothing selected clears the parameter.' : ''}</span>
      </div>
    </div>
  );
}

function ListState({ q, empty }: { q: { isLoading: boolean; isError: boolean; error: unknown; data?: unknown[] }; empty: string }) {
  if (q.isLoading) return <p className="mt-1 text-xs text-darkGrey/60">Loading…</p>;
  if (q.isError) return <p className="mt-1 text-xs text-red">{(q.error as Error)?.message || 'This list is not available. Type the value instead.'}</p>;
  if (!q.data || q.data.length === 0) return <p className="mt-1 text-xs text-darkGrey/60">{empty}</p>;
  return null;
}
